import React from "react";
import { useSelector } from "react-redux";
import "./biweeklyPlatformStats.css";

const BiWeeklyPlatformStats = () => {
  const { reviews } = useSelector((state) => state.reviews);

  const today = new Date();
  const twoWeeksAgo = new Date(today);
  twoWeeksAgo.setDate(today.getDate() - 14);

  const fourWeeksAgo = new Date(twoWeeksAgo);
  fourWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

  // Helper function to calculate star counts
  const calculateStarCounts = (reviews) => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach((review) => {
      const rating = review[4]; // Star rating at index [4]
      if (rating >= 1 && rating <= 5) {
        counts[rating] += 1;
      }
    });
    return counts;
  };

  // Helper function to calculate average rating
  const calculateAverageRating = (reviews) => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, review) => sum + review[4], 0); // Star rating at index [4]
    return (total / reviews.length).toFixed(2);
  };

  // Filter reviews for the last two weeks and the two weeks before that
  const reviewsLastTwoWeeks = reviews.filter((review) => {
    const reviewDate = new Date(review[3].split("/").reverse().join("-")); // Date in 'DD/MM/YYYY'
    return reviewDate >= twoWeeksAgo && reviewDate <= today;
  });

  const reviewsPreviousTwoWeeks = reviews.filter((review) => {
    const reviewDate = new Date(review[3].split("/").reverse().join("-"));
    return reviewDate >= fourWeeksAgo && reviewDate < twoWeeksAgo;
  });

  // Group reviews by product code
  const groupByProductCode = (reviews) => {
    return reviews.reduce((groups, review) => {
      const productCode = review[6].trim(); // Product code at index [6]
      if (!groups[productCode]) {
        groups[productCode] = [];
      }
      groups[productCode].push(review);
      return groups;
    }, {});
  };

  const lastTwoWeeksGrouped = groupByProductCode(reviewsLastTwoWeeks);
  const previousTwoWeeksGrouped = groupByProductCode(reviewsPreviousTwoWeeks);

  // Prepare data for display
  const platformStats = Object.keys(lastTwoWeeksGrouped)
    .sort() // Sort product codes alphabetically
    .map((productCode) => {
      const currentReviews = lastTwoWeeksGrouped[productCode];
      const previousReviews = previousTwoWeeksGrouped[productCode] || [];

      const currentAverage = calculateAverageRating(currentReviews);
      const previousAverage = calculateAverageRating(previousReviews);

      const averageChange =
        previousAverage > 0
          ? (((currentAverage - previousAverage) / previousAverage) * 100).toFixed(2)
          : 0;

      const starCounts = calculateStarCounts(currentReviews);

      return {
        productCode,
        currentAverage,
        averageChange,
        starCounts,
      };
    })
    .filter((stat) => stat.currentAverage > 0); // Exclude products with no reviews

  return (
    <div className="biWeeklyStats">
      <h4 className="heading2">Bi-Weekly Average for Platforms</h4>
      <div className="stats-container">
        {platformStats.map(({ productCode, currentAverage, averageChange, starCounts }) => (
          <div key={productCode} className="stats-card">
            <h5 className="product-code">{productCode}</h5>
            <p className="average-rating">Average Rating: {currentAverage}</p>
            <p
              className={`average-change ${
                averageChange > 0 ? "positive" : averageChange < 0 ? "negative" : ""
              }`}
            >
              {averageChange > 0 ? `+${averageChange}%` : `${averageChange}%`}<span>(vs. last 2 weeks)</span>
            </p>
            {Object.entries(starCounts).map(([star, count]) =>
              count > 0 ? (
                <div key={star} className="star-count">
                  {star}-Star: {count}
                </div>
              ) : null
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BiWeeklyPlatformStats;
